/**
 * SIDEBAR UTILITIES - DigiTrack
 * Obsługa interakcji z sidebarem: gestures, collapsed state, keyboard
 */

import { DrawerComponent } from '@/assets/ts/components/_DrawerComponent'

// ============================================
// SWIPE GESTURES - Mobile
// ============================================
let touchStartX = 0
let touchEndX = 0
let touchStartY = 0
let touchEndY = 0

const handleSwipe = () => {
  const swipeDistanceX = touchEndX - touchStartX
  const swipeDistanceY = Math.abs(touchEndY - touchStartY)

  // Swipe left to close (minimum 50px, mainly horizontal)
  if (swipeDistanceX < -50 && swipeDistanceY < 100 && window.innerWidth <= 767) {
    const aside = document.getElementById('kt_aside')
    if (aside && aside.getAttribute('data-kt-drawer-shown') === 'true') {
      DrawerComponent.getInstance(aside)?.hide()
    }
  }
}

export const initSwipeGestures = () => {
  document.addEventListener('touchstart', (e: TouchEvent) => {
    touchStartX = e.changedTouches[0].screenX
    touchStartY = e.changedTouches[0].screenY
  }, { passive: true })

  document.addEventListener('touchend', (e: TouchEvent) => {
    touchEndX = e.changedTouches[0].screenX
    touchEndY = e.changedTouches[0].screenY
    handleSwipe()
  }, { passive: true })
}

// ============================================
// ESC KEY HANDLER - Mobile
// ============================================
export const initEscapeHandler = () => {
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && window.innerWidth <= 767) {
      const aside = document.getElementById('kt_aside')
      if (aside && aside.getAttribute('data-kt-drawer-shown') === 'true') {
        DrawerComponent.getInstance(aside)?.hide()
      }
    }
  })
}

// ============================================
// COLLAPSED STATE - LocalStorage
// ============================================
export const saveCollapsedState = (isCollapsed: boolean) => {
  localStorage.setItem('sidebar-collapsed', isCollapsed ? 'true' : 'false')
}

export const getCollapsedState = (): boolean => {
  return localStorage.getItem('sidebar-collapsed') === 'true'
}

export const restoreCollapsedState = () => {
  const isMobile = window.innerWidth <= 767

  if (!isMobile) {
    const isCollapsed = getCollapsedState()
    const body = document.body

    if (isCollapsed) {
      body.setAttribute('data-kt-aside-minimize', 'on')
    } else {
      body.removeAttribute('data-kt-aside-minimize')
    }
  }
}

// ============================================
// RESIZE HANDLER
// ============================================
let resizeTimeout: number | null = null

export const initResizeHandler = () => {
  window.addEventListener('resize', () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }

    resizeTimeout = window.setTimeout(() => {
      const isMobile = window.innerWidth <= 767
      const body = document.body
      const aside = document.getElementById('kt_aside')

      if (!isMobile) {
        // Desktop: restore collapsed state
        if (aside) {
          DrawerComponent.getInstance(aside)?.hide()
        }
        restoreCollapsedState()
      } else {
        // Mobile: remove collapsed state
        body.removeAttribute('data-kt-aside-minimize')
      }
    }, 250)
  })
}

// ============================================
// TOGGLE OBSERVER - Track collapse state
// ============================================
export const observeToggleState = () => {
  const body = document.body

  // Obserwuj zmiany atrybutu data-kt-aside-minimize
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-kt-aside-minimize') {
        const isMinimized = body.getAttribute('data-kt-aside-minimize') === 'on'
        saveCollapsedState(isMinimized)
      }
    })
  })

  observer.observe(body, {
    attributes: true,
    attributeFilter: ['data-kt-aside-minimize']
  })
}

// ============================================
// PEEK SUPPRESSION - Desktop minimize
// The minimize toggle lives inside the hoverable aside, so right after a
// collapse the cursor still rests on the aside and the peek-on-hover rule
// re-expands it - making the click look like a no-op. Suppress the peek
// (via `.aside-peek-off`) until the pointer leaves the aside, so the collapse
// is immediately visible.
// ============================================
export const initPeekSuppression = () => {
  const aside = document.getElementById('kt_aside')
  const toggle = document.getElementById('kt_aside_toggle')
  if (!aside || !toggle) {
    return
  }

  toggle.addEventListener('click', () => {
    aside.classList.add('aside-peek-off')
  })

  aside.addEventListener('mouseleave', () => {
    aside.classList.remove('aside-peek-off')
  })
}

// ============================================
// FOCUS TRAP - Mobile menu accessibility
// ============================================
const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const initFocusTrap = () => {
  const aside = document.getElementById('kt_aside')
  if (!aside) {
    console.warn('initFocusTrap: kt_aside not found')
    return
  }

  let activeHandler: ((e: KeyboardEvent) => void) | null = null

  const observer = new MutationObserver(() => {
    const isOpen = aside.getAttribute('data-kt-drawer-shown') === 'true'

    // Remove previous handler
    if (activeHandler) {
      aside.removeEventListener('keydown', activeHandler)
      activeHandler = null
    }

    if (isOpen && window.innerWidth <= 767) {
      const focusableElements = aside.querySelectorAll(focusableSelectors)
      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (!firstElement || !lastElement) return

      // Focus first element
      firstElement?.focus()

      // Trap focus
      activeHandler = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus()
            e.preventDefault()
          }
        }
      }

      aside.addEventListener('keydown', activeHandler)
    }
  })

  observer.observe(aside, {
    attributes: true,
    attributeFilter: ['data-kt-drawer-shown']
  })
}

// ============================================
// INITIALIZE ALL
// ============================================
export const initSidebar = () => {
  // Restore collapsed state on load
  restoreCollapsedState()

  // Initialize handlers
  initSwipeGestures()
  initEscapeHandler()
  initResizeHandler()
  observeToggleState()
  initPeekSuppression()
  initFocusTrap()
}

// ============================================
// AUTO-CLOSE ON ROUTE CHANGE (Mobile)
// ============================================
export const closeMobileMenuOnRouteChange = () => {
  const aside = document.getElementById('kt_aside')
  if (aside && window.innerWidth <= 767) {
    DrawerComponent.getInstance(aside)?.hide()
  }
}
